import React from 'react'
import { useMediaQuery, Theme, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import Link, { LinkProps } from '../Link'
import {
  ExternalLink,
  NavigationGroupLink as NavigationGroupLinkType,
  TargetOptions,
} from '../../types/page'
import NavigationButton from '../Buttons/NavigationButton'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import JumpLink from '../Link/JumpLink'
import { getExternalLinkURL, getRawUrlAndSearchFromUrl } from '../../utils/url'
import { BrandNames } from '../../types/brand'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'
import insertHandlebarsTemplate from '../../utils/insertHandlebarsTemplate'
import { useMixpanelCTAClickTracking } from '../../utils/mixpanel'
import useSiteMetadata from '../../hooks/useSiteMetadata'
import useTidyLink from '../../hooks/useTidyLink'
import { JumpLink as JumpLinkType } from '../../types/post'

type Props = Omit<LinkProps, 'to' | 'href'> & {
  link: NavigationGroupLinkType
  children?: React.ReactNode
}

export const NavigationGroupLink: React.FC<Props> = ({
  link,
  children,
  ...linkProps
}) => {
  const { blogPrefix, urlPathBuilder } = useUrlPathBuilder()
  const { brandName } = useSiteMetadata()
  const handlebarsContext = useHandlebarsContext()
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('lg')
  )
  const { classes } = useLinkStyles()
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()
  const tidyTheLink = useTidyLink()

  if (link.__typename === 'ContentfulButton') {
    const { onClick } = linkProps
    return (
      <NavigationButton
        link={link}
        className={linkProps.className}
        onClick={onClick}
      />
    )
  }

  if (
    'url' in link &&
    'text' in link &&
    // Identify the contentful entry based on it's name
    link.text === 'Account' &&
    brandName.toLowerCase() !== BrandNames.zipJob
  ) {
    return (
      <Link
        href={getExternalLinkURL(link)}
        target={
          link.openTheLinkIn === TargetOptions.NewTab ? '_blank' : '_self'
        }
        {...linkProps}
        onAuxClick={(e) => {
          e.stopPropagation()

          if (linkProps.onClick) {
            linkProps.onClick(e)
          }

          handleMpCTAClickTracking(
            e.currentTarget.innerText,
            tidyTheLink(link.url),
            'mixpanelTracking' in link ? link?.mixpanelTracking : null
          )
        }}
        onClick={(e) => {
          e.stopPropagation()

          if (linkProps.onClick) {
            linkProps.onClick(e)
          }

          handleMpCTAClickTracking(
            e.currentTarget.innerText,
            tidyTheLink(link.url),
            'mixpanelTracking' in link ? link?.mixpanelTracking : null
          )
        }}
      >
        {link.icon && !isSmallScreen ? (
          <img
            alt={link.text}
            src={link.icon.file.url}
            width={link.icon.file.details.image.width}
            height={link.icon.file.details.image.height}
          />
        ) : (
          // Render the text "Account" when on smaller screens for TR/TCV
          link.text
        )}
      </Link>
    )
  }

  if ('url' in link) {
    return (
      <Link
        href={link.url}
        target={
          link.openTheLinkIn === TargetOptions.NewTab ? '_blank' : '_self'
        }
        {...linkProps}
        onAuxClick={(e) => {
          e.stopPropagation()

          if (linkProps.onClick) {
            linkProps.onClick(e)
          }

          handleMpCTAClickTracking(
            e.currentTarget.innerText,
            tidyTheLink(link.url),
            'mixpanelTracking' in link ? link?.mixpanelTracking : null
          )
        }}
        onClick={(e) => {
          e.stopPropagation()

          if (linkProps.onClick) {
            linkProps.onClick(e)
          }

          handleMpCTAClickTracking(
            e.currentTarget.innerText,
            tidyTheLink(link.url),
            'mixpanelTracking' in link ? link?.mixpanelTracking : null
          )
        }}
      >
        {!isSmallScreen && link.icon ? (
          <Box className={classes.linkIconContainer}>
            {
              <img
                alt={link.text}
                src={link.icon.file.url}
                height={24}
                width={24}
              />
            }
            {link.text}
          </Box>
        ) : children ? (
          children
        ) : (
          insertHandlebarsTemplate(link.text, handlebarsContext)
        )}
      </Link>
    )
  }

  if ('action' in link && link.action) {
    if ('url' in link.action) {
      return (
        <Link
          key={link.action.url}
          href={link.action.url}
          {...linkProps}
          onAuxClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTheLink((link.action as ExternalLink)?.url),
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
          onClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTheLink((link.action as ExternalLink)?.url),
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
        >
          {link.text}
        </Link>
      )
    }
    if ('hash' in link.action) {
      return (
        <JumpLink
          hash={link.action.hash}
          {...linkProps}
          onAuxClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              (link.action as JumpLinkType).hash,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
          onClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              (link.action as JumpLinkType).hash,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
        >
          {link.text}
        </JumpLink>
      )
    }
    if ('slug' in link.action) {
      const [rawUrl, queryParams] = getRawUrlAndSearchFromUrl(
        link.action.slug ?? ''
      )
      let paths = [rawUrl]

      if (link.action.__typename === 'ContentfulPost') {
        paths = [blogPrefix, link.action.slug]
      }

      if (link.action.__typename === 'ContentfulCategory') {
        paths = [blogPrefix, 'category', link.action.slug]
      }
      const tidyTo = tidyTheLink(
        urlPathBuilder(paths, {
          search: queryParams,
        })
      )
      return (
        <Link
          to={tidyTo}
          {...linkProps}
          onAuxClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTo,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
          onClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTo,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
        >
          {link.text}
        </Link>
      )
    }
  }

  if ('slug' in link) {
    const [rawUrl, queryParams] = getRawUrlAndSearchFromUrl(link.slug ?? '')
    let paths = [rawUrl]
    let text = ''

    switch (link.__typename) {
      case 'ContentfulCategory':
        paths = [blogPrefix, 'category', link.slug]
        text = link.name
        break
      case 'ContentfulPost':
        paths = [blogPrefix, link.slug]
        text = link.title
        break
      case 'ContentfulInternalLink':
        text = link.text ?? ''
        break
    }

    if ('icon' in link && link.icon != null && !isSmallScreen) {
      const tidyTo = tidyTheLink(
        urlPathBuilder(paths, {
          search: queryParams,
        })
      )
      return (
        <Link
          to={tidyTo}
          {...linkProps}
          onAuxClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTo,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
          onClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTo,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
        >
          <Box className={classes.linkIconContainer}>
            {
              <img
                alt={link.text}
                src={link.icon.file.url}
                height={24}
                width={24}
              />
            }
            {link.text}
          </Box>
        </Link>
      )
    }

    //Work around for MUI 4 otherwise children will always be rendered
    if (
      (Array.isArray(children) &&
        Array.isArray(children[0]) &&
        children[0].length) ||
      (children && !Array.isArray(children))
    ) {
      return (
        <Link
          to={urlPathBuilder(paths, {
            search: queryParams,
          })}
          {...linkProps}
          onAuxClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTo,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
          onClick={(e) => {
            e.stopPropagation()

            if (linkProps.onClick) {
              linkProps.onClick(e)
            }

            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTo,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }}
        >
          {children}
        </Link>
      )
    }

    const tidyTo = tidyTheLink(
      urlPathBuilder(paths, {
        search: queryParams,
      })
    )

    return (
      <Link
        // Fix to use /resume-writing page as an External Link 
        {...(tidyTo === "/resume-writing" ? { href: tidyTo } : { to: tidyTo })}
        {...linkProps}
        onAuxClick={(e) => {
          e.stopPropagation()

          if (linkProps.onClick) {
            linkProps.onClick(e)
          }

          if (link.__typename === 'ContentfulInternalLink') {
            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTo,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }
        }}
        onClick={(e) => {
          e.stopPropagation()

          if (linkProps.onClick) {
            linkProps.onClick(e)
          }

          if (link.__typename === 'ContentfulInternalLink') {
            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              tidyTo,
              'mixpanelTracking' in link ? link?.mixpanelTracking : null
            )
          }
        }}
      >
        {text}
      </Link>
    )
  }

  return null
}

const useLinkStyles = makeStyles()((theme) => ({
  linkIconContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}))

export default NavigationGroupLink
